import { motion, useReducedMotion } from "framer-motion";

// Abstract campaign-system flow — Ad → Landing Page → Lead → Follow-Up → Reporting.
// Same visual language as GrowthChart: thin lines, restrained animation, no fake numbers.
export default function CampaignFlowDiagram() {
  const reduce = useReducedMotion();

  const nodes = [
    { x: 60,  label: "AD" },
    { x: 160, label: "LANDING" },
    { x: 260, label: "LEAD" },
    { x: 360, label: "FOLLOW-UP" },
    { x: 460, label: "REPORTING" },
  ];
  const y = 170;

  return (
    <div
      className="relative w-full"
      data-testid="campaign-flow-diagram"
      aria-hidden="true"
    >
      <div className="pointer-events-none absolute right-6 top-6 h-40 w-40 rounded-full bg-fs-gold/10 blur-3xl" />

      <svg viewBox="0 0 520 340" className="relative w-full" style={{ overflow: "visible" }}>
        {/* fine grid */}
        <g stroke="rgba(255,255,255,0.05)" strokeWidth="1">
          {[60, 120, 180, 240, 300].map((gy) => (
            <line key={`h${gy}`} x1="20" y1={gy} x2="500" y2={gy} />
          ))}
          {[80, 160, 240, 320, 400, 480].map((gx) => (
            <line key={`v${gx}`} x1={gx} y1="40" x2={gx} y2="300" />
          ))}
        </g>

        {/* connecting line, draws once */}
        <motion.path
          d={`M ${nodes[0].x} ${y} L ${nodes[nodes.length - 1].x} ${y}`}
          fill="none"
          stroke="#C5A24A"
          strokeWidth="1.5"
          strokeLinecap="round"
          initial={reduce ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0.9 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, delay: 0.3, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 0 4px rgba(197,162,74,0.25))" }}
        />

        {/* subtle secondary arc, hints at data feedback loop */}
        <motion.path
          d={`M ${nodes[nodes.length - 1].x} ${y - 6} Q 260 60, ${nodes[0].x} ${y - 6}`}
          fill="none"
          stroke="rgba(197,162,74,0.25)"
          strokeWidth="1"
          strokeDasharray="3 4"
          initial={reduce ? { pathLength: 1, opacity: 0.6 } : { pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 0.6 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, delay: 1.6, ease: "easeInOut" }}
        />

        {/* nodes */}
        {nodes.map((n, i) => (
          <motion.g
            key={n.label}
            initial={reduce ? { opacity: 1 } : { opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.4 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            <circle cx={n.x} cy={y} r="10" fill="#0f0f0f" stroke="#C5A24A" strokeWidth="1.5" />
            <circle cx={n.x} cy={y} r="3" fill="#C5A24A" />
            <text
              x={n.x}
              y={y + 34}
              textAnchor="middle"
              fill="#A8A39A"
              fontSize="9"
              letterSpacing="2"
              fontFamily="Manrope, sans-serif"
            >
              {n.label}
            </text>
            <text
              x={n.x}
              y={y - 24}
              textAnchor="middle"
              fill="#C5A24A"
              fontSize="10"
              fontFamily="'Cormorant Garamond', serif"
              fontStyle="italic"
            >
              0{i + 1}
            </text>
          </motion.g>
        ))}

        {/* subtle illuminated final node */}
        <motion.circle
          cx={nodes[nodes.length - 1].x}
          cy={y}
          r="16"
          fill="none"
          stroke="#C5A24A"
          strokeWidth="1"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.55 }}
          viewport={{ once: true }}
          transition={{ delay: 1.5, duration: 0.6 }}
        />
      </svg>
    </div>
  );
}
